// src/components/Monografia/MonografiaList.jsx
import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { listarMonografias } from '../../services/monografiaService';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Pagination
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Clear as ClearIcon, CalendarToday as CalendarIcon } from '@mui/icons-material';
import ChatIcon from '@mui/icons-material/Chat';

const MonografiaList = ({ onOpenChat }) => {
  // Estados de dados e controle
  const [monografias, setMonografias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filtros
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Paginação
  const [page, setPage] = useState(1);
  const pageSize = 5; // 5 monografias por página

  const navigate = useNavigate();

  // Efeito para carregar monografias
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await listarMonografias();
        setMonografias(response.data || []);
      } catch (error) {
        console.error('Erro ao carregar monografias:', error);
        setError('Não foi possível carregar as monografias.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // useMemo para filtrar e ordenar as monografias
  const monografiasFiltradas = useMemo(() => {
    let lista = [...monografias];

    // Ordenar por ID (pode ajustar conforme necessário)
    lista.sort((a, b) => a.id - b.id);

    // Filtrar por status
    if (statusFilter) {
      lista = lista.filter((m) => m.status === statusFilter);
    }

    // Filtrar por texto no campo de título (proposta)
    if (searchTerm.trim()) {
      const lowerSearch = searchTerm.toLowerCase();
      lista = lista.filter((m) => {
        const titulo = m.projeto?.proposta?.titulo?.toLowerCase() || '';
        return titulo.includes(lowerSearch);
      });
    }

    return lista;
  }, [monografias, statusFilter, searchTerm]);

  // Cálculo de paginação
  const totalMonografias = monografiasFiltradas.length;
  const totalPages = Math.ceil(totalMonografias / pageSize);

  // Recortar a lista de acordo com a página
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const monografiasPagina = monografiasFiltradas.slice(startIndex, endIndex);

  // Ajuste de página se necessário
  useEffect(() => {
    if (page > totalPages && totalPages > 0) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!monografias || monografias.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ m: 2 }}>
        Nenhuma monografia encontrada.
      </Typography>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
        Lista de Monografias
      </Typography>

      {/* Barra de Filtros */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
          mb: 3
        }}
      >
        {/* Filtro por status */}
        <FormControl sx={{ minWidth: 140 }} size="small">
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
          >
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="RASCUNHO">Rascunho</MenuItem>
            <MenuItem value="SUBMETIDA">Submetida</MenuItem>
            <MenuItem value="APROVADA">Aprovada</MenuItem>
            <MenuItem value="REJEITADA">Rejeitada</MenuItem>
          </Select>
        </FormControl>

        {/* Busca por título */}
        <TextField
          size="small"
          label="Buscar por título"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(1);
          }}
          sx={{ width: 220 }}
        />

        {/* Botão de limpar filtros */}
        <Button
          variant="outlined"
          startIcon={<ClearIcon />}
          onClick={() => {
            setStatusFilter('');
            setSearchTerm('');
            setPage(1);
          }}
        >
          Limpar Filtros
        </Button>

        {/* Contador */}
        <Typography variant="body2" sx={{ ml: 'auto' }}>
          Monografias encontradas: <strong>{totalMonografias}</strong>
        </Typography>
      </Box>

      {monografiasFiltradas.length === 0 ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          Nenhuma monografia corresponde aos filtros selecionados.
        </Alert>
      ) : (
        <>
          {monografiasPagina.map((m) => (
            <Paper
              key={m.id}
              elevation={2}
              sx={{
                p: 2,
                mb: 2,
                borderRadius: 2,
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                transition: 'box-shadow 0.2s, transform 0.2s',
                '&:hover': {
                  boxShadow: 6,
                  transform: 'scale(1.01)',
                }
              }}
            >
              <Box>
                {/* Exibe o TÍTULO do tema (proposta) */}
                <Typography variant="subtitle1" gutterBottom>
                  <strong>Título:</strong> {m.projeto?.proposta?.titulo || 'Sem Título'}
                </Typography>

                {/* Exibe o nome do Estudante */}
                {m.projeto?.proposta?.estudante?.nome && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <strong>Aluno:</strong> {m.projeto.proposta.estudante.nome}
                  </Typography>
                )}

                {/* Exemplo de exibição da data de submissão */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <CalendarIcon color="action" fontSize="small" />
                  <Typography variant="body2">
                    {new Date(m.data_submissao).toLocaleDateString()}
                  </Typography>
                </Box>

                {/* Exibe o status */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <Typography variant="body2">
                    <strong>Status:</strong>
                  </Typography>
                  <Chip
                    label={m.status}
                    size="small"
                    color={
                      m.status === 'RASCUNHO'
                        ? 'warning'
                        : m.status === 'APROVADA'
                        ? 'success'
                        : m.status === 'SUBMETIDA'
                        ? 'primary'
                        : m.status === 'REJEITADA'
                        ? 'error'
                        : 'default'
                    }
                  />
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: { xs: 2, sm: 0 } }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate(`/monografias/${m.id}`)}
                >
                  Ver Detalhes
                </Button>
                {onOpenChat && (
                  <Button
                    variant="outlined"
                    startIcon={<ChatIcon />}
                    onClick={() => onOpenChat(m, 'monografia')}
                  >
                    Chat
                  </Button>
                )}
              </Box>
            </Paper>
          ))}

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination
              color="primary"
              count={totalPages}
              page={page}
              onChange={(_, newPage) => setPage(newPage)}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

MonografiaList.propTypes = {
  onOpenChat: PropTypes.func,
};

export default MonografiaList;
